import {
  notificationControllerListMine,
  notificationControllerMarkAllRead,
  notificationControllerMarkOneRead,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  AlignLeft,
  Bell,
  Check,
  Command,
  LogOut,
  Moon,
  Search,
  Sun,
  UserCircle,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useSidebar } from '../../context/sidebar-context';
import { useCommandPalette } from '../../hooks/use-command-palette';
import { usePermissions } from '../../hooks/use-permissions';
import { type Theme, useTheme } from '../../hooks/use-theme';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import { NotificationMockPanel } from '../dev/notification-mock-panel';
import { GradientAvatar } from '../shared/gradient-avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { UserSwitcher } from './user-switcher';

// ── Types ──────────────────────────────────────────────────────────────────

type InAppNotifItem = {
  id: string;
  type: string;
  title: string;
  read: boolean;
  readAt: string | null;
  createdAt: string;
  requestId: string | null;
  targetTab: 'inspection' | 'pledge' | 'details' | 'study' | 'lab' | 'siteVisit' | null;
  request: { referenceNo: string } | null;
};

type ListMineResponse = {
  items: InAppNotifItem[];
  total: number;
  unreadCount: number;
};

// ── Helpers ────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── NotificationDropdown ───────────────────────────────────────────────────

const QUERY_KEY = ['notif-mine'];

function NotificationDropdown() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      notificationControllerListMine({
        limit: 10,
        page: 1,
      }) as unknown as Promise<ListMineResponse>,
    enabled: !!user,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  const markOne = useMutation({
    mutationFn: (id: string) => notificationControllerMarkOneRead({ id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const markAll = useMutation({
    mutationFn: () => notificationControllerMarkAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const [testStatus, setTestStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const testPush = useMutation({
    mutationFn: () => api.post('notifications/me/test-push'),
    onSuccess: () => {
      setTestStatus('sent');
      setTimeout(() => setTestStatus('idle'), 2000);
    },
    onError: () => setTestStatus('error'),
  });

  const STUDY_NOTIF_TYPES = new Set([
    'STUDY_REPORT_SUBMITTED',
    'STUDY_REPORT_APPROVED',
    'STUDY_REPORT_REJECTED',
    'LAB_TEST_REQUESTED',
    'LAB_REPORT_SUBMITTED',
    'STUDY_REQUEST_RECEIVED',
  ]);

  // Maps InAppNotificationTab (Prisma enum) values to the study-request route's
  // own `tab` search-param values, which use different names (e.g. lab -> lab-requests).
  const STUDY_TARGET_TAB_MAP: Record<string, string> = {
    study: 'study-report',
    lab: 'lab-requests',
    siteVisit: 'site-visits',
    details: 'general',
  };

  const handleItemClick = (n: InAppNotifItem) => {
    if (!n.read) markOne.mutate(n.id);
    if (n.requestId) {
      if (n.targetTab === 'study' || STUDY_NOTIF_TYPES.has(n.type)) {
        const tab = (n.targetTab && STUDY_TARGET_TAB_MAP[n.targetTab]) ?? 'study-report';
        navigate({
          to: '/study-requests/$requestId',
          params: { requestId: n.requestId },
          // biome-ignore lint/suspicious/noExplicitAny: tab value is dynamic at runtime
          search: { tab: tab as any },
        });
      } else {
        navigate({
          to: '/permits/$permitId',
          params: { permitId: n.requestId },
          // biome-ignore lint/suspicious/noExplicitAny: tab value is dynamic at runtime
          search: n.targetTab ? { tab: n.targetTab as any } : {},
        });
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors lg:h-11 lg:w-11"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-error-500 text-[9px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-80 p-0 bg-white border border-gray-200 shadow-xl rounded-xl"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-900">Notifications</span>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="text-xs font-medium text-brand-600 hover:text-brand-700 transition-colors disabled:opacity-50"
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Empty state */}
        {items.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-gray-400">No notifications yet.</div>
        )}

        {/* Items */}
        {items.length > 0 && (
          <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
            {items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleItemClick(n)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                {/* Unread dot */}
                <div className="mt-1.5 flex h-2 w-2 shrink-0 items-center justify-center">
                  {!n.read && <span className="h-2 w-2 rounded-full bg-brand-500" />}
                </div>

                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-xs leading-snug',
                      n.read ? 'font-normal text-gray-600' : 'font-semibold text-gray-900',
                    )}
                  >
                    {n.title}
                  </p>
                  <div className="mt-1 flex items-center gap-1.5">
                    {n.request?.referenceNo && (
                      <span className="rounded-md bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-600">
                        {n.request.referenceNo}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">{timeAgo(n.createdAt)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Dev-only: test push trigger */}
        {import.meta.env.DEV && (
          <div className="border-t border-dashed border-amber-200 bg-amber-50 p-2">
            <button
              type="button"
              onClick={() => testPush.mutate()}
              disabled={testPush.isPending}
              className="w-full rounded-lg px-4 py-2 text-center text-xs font-medium text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
            >
              {testPush.isPending && 'Sending…'}
              {!testPush.isPending && testStatus === 'sent' && '✓ Push sent — check for OS popup'}
              {!testPush.isPending && testStatus === 'error' && 'Failed — see console'}
              {!testPush.isPending && testStatus === 'idle' && '⚡ Send test push (dev only)'}
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-gray-100 p-2">
          <button
            type="button"
            onClick={() => navigate({ to: '/notifications' })}
            className="w-full rounded-lg px-4 py-2.5 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            View all
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── ThemeDropdown ──────────────────────────────────────────────────────────

type ThemeGroup = {
  label: string;
  light: Theme;
  dark: Theme;
};

const THEME_GROUPS: ThemeGroup[] = [
  { label: 'Kalba', light: 'kalba_light', dark: 'kalba_dark' },
  { label: 'Sharjah', light: 'shj_light', dark: 'shj_dark' },
  { label: 'Digital Sharjah', light: 'ds_light', dark: 'ds_dark' },
  { label: 'BEEAH', light: 'beeah_corp_light', dark: 'beeah_corp_dark' },
  { label: 'BEEAH-ENV', light: 'beeah_light', dark: 'beeah_dark' },
  { label: 'Kalba New SHJ', light: 'kalba_new_shj_light', dark: 'kalba_new_shj_dark' },
];

function ThemeDropdown({ theme, setTheme }: { theme: Theme; setTheme: (t: Theme) => void }) {
  const isDark = THEME_GROUPS.some((g) => g.dark === theme);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors lg:h-11 lg:w-11"
          aria-label="Select theme"
        >
          {isDark ? <Moon size={18} /> : <Sun size={18} />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48" sideOffset={8}>
        <DropdownMenuLabel className="text-xs text-gray-400 font-medium uppercase tracking-wide px-2 py-1.5">
          Theme
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {THEME_GROUPS.map((group) => {
          const isActive = theme === group.light || theme === group.dark;
          return (
            <DropdownMenuSub key={group.label}>
              <DropdownMenuSubTrigger className="flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm">
                <span className="flex-1">{group.label}</span>
                {isActive && <Check size={13} className="text-primary shrink-0 mr-1" />}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-32">
                <DropdownMenuItem
                  onClick={() => setTheme(group.light)}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                >
                  <Sun size={13} className="text-gray-500 shrink-0" />
                  <span className="flex-1 text-sm">Light</span>
                  {theme === group.light && <Check size={13} className="text-primary shrink-0" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTheme(group.dark)}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                >
                  <Moon size={13} className="text-gray-500 shrink-0" />
                  <span className="flex-1 text-sm">Dark</span>
                  {theme === group.dark && <Check size={13} className="text-primary shrink-0" />}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── UserDropdown ───────────────────────────────────────────────────────────

function UserDropdown() {
  const { user, clearSession } = useAuthStore();
  const { isSharjahOnly } = usePermissions();
  const navigate = useNavigate();

  const handleSignOut = () => {
    clearSession();
    navigate({ to: isSharjahOnly ? '/sm/login' : '/login' });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-full overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity lg:h-11 lg:w-11"
          aria-label="User menu"
        >
          <GradientAvatar
            name={user?.name ?? 'U'}
            avatarUrl={user?.avatarUrl ?? '/images/arabian.png'}
            size="md"
            shape="circle"
            className="h-full w-full"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
        <DropdownMenuLabel className="font-normal px-3 py-2">
          <p className="text-sm font-semibold text-gray-900 leading-tight">{user?.name}</p>
          <p className="mt-0.5 text-xs text-gray-400 truncate">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate({ to: '/settings/profile' })}
          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
        >
          <UserCircle size={15} className="text-gray-400 shrink-0" />
          Profile settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
        >
          <LogOut size={15} className="shrink-0" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Header ─────────────────────────────────────────────────────────────────

export function Header() {
  const { isExpanded, toggleSidebar, toggleMobileSidebar, isMobileOpen } = useSidebar();
  const { theme, setTheme } = useTheme();
  const { setOpen: openPalette } = useCommandPalette();

  const handleToggle = () => {
    if (window.innerWidth >= 1024) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  const marginLeft = isExpanded ? 'lg:ml-64' : 'lg:ml-20';

  return (
    <header
      className={cn(
        'fixed top-0 right-0 left-0 z-[9999] flex h-16 items-center border-b border-gray-200 bg-white transition-all duration-300 ease-in-out',
        marginLeft,
      )}
    >
      <div className="flex w-full items-center justify-between px-4 lg:px-6">
        {/* Left: toggle + search */}
        <div className="flex items-center gap-3">
          {/* Sidebar toggle */}
          <button
            type="button"
            onClick={handleToggle}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors lg:h-11 lg:w-11"
            aria-label="Toggle sidebar"
          >
            {isMobileOpen ? <X size={20} /> : <AlignLeft size={20} />}
          </button>

          {/* Search trigger — desktop (opens command palette) */}
          <button
            type="button"
            onClick={() => openPalette(true)}
            className="relative hidden h-11 w-64 items-center rounded-lg border border-gray-200 bg-transparent py-2.5 pl-10 pr-14 text-sm text-gray-400 transition-colors hover:border-brand-300 hover:bg-gray-50 md:flex xl:w-[380px]"
          >
            <Search
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <span>Search or type command...</span>
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-1 text-[10px] text-gray-500">
              <Command size={10} />K
            </kbd>
          </button>

          {/* Search icon — mobile */}
          <button
            type="button"
            onClick={() => openPalette(true)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-50 md:hidden"
          >
            <Search size={18} />
          </button>
        </div>

        {/* Right: user switch + theme + notifications */}
        <div className="flex items-center gap-2">
          {/* User switcher (dev/QA only — Super Admin) */}
          <UserSwitcher />

          {/* Theme picker */}
          <ThemeDropdown theme={theme} setTheme={setTheme} />

          {/* Dev-only: mock notification triggers */}
          {import.meta.env.DEV && <NotificationMockPanel />}

          {/* Notifications */}
          <NotificationDropdown />

          {/* User avatar */}
          <UserDropdown />
        </div>
      </div>
    </header>
  );
}
